import React, { type FC, type ReactNode, useMemo, useRef } from "react";
import classNames from "classnames";
import { Button } from "@shared/ui/button";
import { Icon } from "@shared/ui/icon";
import { arrayIsEmpty } from "@src/helpers/array/arrayIsEmpty";
import { ListItem } from "./ListItem";
import "./List.scss";

/** Кнопка для худа списка. */
export interface ListHoodButton {
    /** Текст кнопки. */
    text: string;
    /**
     * Путь до иконки.
     * @deprecated Предпочитай {@link ListHoodButton.icon} с компонентом из `@omega/icons`.
     */
    srcIcon?: string;
    /** React-иконка (предпочтительно `@omega/icons`). */
    icon?: ReactNode;
    /**
     * Скрыть кнопку для отображения.
     * @default false
     */
    hide?: boolean;
    /** Обработчик нажатия. */
    onClick?: () => void;
}

/** Параметры бесконечной прокрутки. */
export interface InfiniteScrollOptionsProps {
    /**
     * Лимит элементов за один запрос. \
     * Если сервер вернул меньше `limit` или пустой массив — данные закончились.
     */
    limit?: number;
    /**
     * Порог прокрутки в процентах, при котором грузятся следующие элементы. \
     * Например, `90` — загрузка на 90% высоты.
     */
    loadMoreScrollTrigger?: number;
    /**
     * Загрузка следующих элементов при скролле.
     *
     * @param limit — размер страницы
     *
     * Функция должна быть async и **await**-ить запрос, иначе блокировка на время загрузки не сработает.
     */
    loadMoreItems?: (limit: number) => Promise<void>;
}

/** Пропсы контейнера списка. */
export interface ListProps {
    className?: string;
    children?: ReactNode[];
    /** Кнопки над списком. */
    hoodBtns?: ListHoodButton[];
    /** Состояние загрузки. */
    isLoad?: boolean;
    /**
     * Список ещё не запрашивался. \
     * При пустых `children` показывает idle вместо «список пуст».
     * @default false
     */
    idle?: boolean;
    /**
     * Текст idle-состояния.
     * @default "Укажите параметры поиска"
     */
    idleText?: string;
    /**
     * Текст пустого результата после запроса.
     * @default "Список пуст..."
     */
    emptyText?: string;
    /** Кнопка действия в idle-состоянии (например «К фильтру»). */
    idleAction?: Pick<ListHoodButton, "text" | "srcIcon" | "icon" | "onClick">;
    /** Настройки бесконечной прокрутки. */
    infinityScroll?: InfiniteScrollOptionsProps;
}

/**
 * Scroll-контейнер списка: hood-кнопки, idle/empty, skeleton, infinite scroll. \
 * Экраны обычно используют {@link BusinessList}, а не `List` напрямую.
 */
export const List: FC<ListProps> = ({
    className,
    isLoad,
    children,
    hoodBtns,
    idle = false,
    idleText = "Укажите параметры поиска",
    emptyText = "Список пуст...",
    idleAction,
    infinityScroll: { limit = 100, loadMoreScrollTrigger = 100, loadMoreItems = undefined } = {},
}) => {
    const loadingSkeleton = (
        <>
            <ListItem isLoad title="" onClick={() => {}} />
            <ListItem isLoad title="" onClick={() => {}} />
            <ListItem isLoad title="" onClick={() => {}} />
            <ListItem isLoad title="" onClick={() => {}} />
        </>
    );

    const noContent = idle ? (
        <div className="list_no_content list_no_content--idle">
            <span className="list_no_content__text">{idleText}</span>
            {idleAction != null && (
                <Button
                    size="sm"
                    variant="primary"
                    startSlot={
                        idleAction.icon ??
                        (idleAction.srcIcon != null ? <Icon src={idleAction.srcIcon} size="sm" /> : undefined)
                    }
                    onClick={idleAction.onClick}
                >
                    {idleAction.text}
                </Button>
            )}
        </div>
    ) : (
        <div className="list_no_content">
            <span className="list_no_content__text">{emptyText}</span>
        </div>
    );

    const content = !arrayIsEmpty(children) ? children : noContent;

    const listRef = useRef<HTMLDivElement>(null);
    const isLockLoadItems = useRef<boolean>(false);

    const visibleHoodBtns = useMemo<ListHoodButton[] | null>(() => {
        if (arrayIsEmpty(hoodBtns)) return null;
        return hoodBtns.filter((x) => x.hide !== true);
    }, [hoodBtns]);

    const loadMoreItemsOnScroll = async (scrollPosition: number, loadTriggerPoint: number): Promise<void> => {
        if (scrollPosition >= loadTriggerPoint && !isLockLoadItems.current && loadMoreItems != null) {
            isLockLoadItems.current = true;
            await loadMoreItems(limit);
            isLockLoadItems.current = false;
        }
    };

    const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
        const target = event.currentTarget;
        const { scrollTop, scrollHeight, clientHeight } = target;
        const scrollPosition = scrollTop + clientHeight;
        const triggerPoint = scrollHeight * (loadMoreScrollTrigger / 100);

        void loadMoreItemsOnScroll(scrollPosition, triggerPoint);
    };

    return (
        <div className={classNames("list_container", className)}>
            {visibleHoodBtns != null && (
                <div className="list_hood">
                    {visibleHoodBtns.map((btn, index) => (
                        <Button
                            key={index}
                            size="sm"
                            variant="primary"
                            startSlot={btn.icon ?? (btn.srcIcon != null ? <Icon src={btn.srcIcon} size="sm" /> : undefined)}
                            onClick={btn.onClick}
                        >
                            {btn.text}
                        </Button>
                    ))}
                </div>
            )}
            <div className="list custom_scrollbar" ref={listRef} onScroll={handleScroll}>
                {isLoad === true ? loadingSkeleton : content}
            </div>
        </div>
    );
};
